import { Badge, formatNumber, type Column } from '@platform/design-system';
// import { Badge, formatNumber, IconButton, type Column } from '@platform/design-system';
import { ChevronRight, Folder } from 'lucide-react';
// import { Archive, ChevronRight, Folder, Pencil } from 'lucide-react';
import type { CSSProperties } from 'react';
import { usePageContext } from '../usePageContext.ts';
import { nameIn, type Category, type CategoryNode } from './categories.ts';
import styles from './Categories.module.css';

export type CategoryColumnsInput = {
  readonly locales: readonly string[];
  readonly parents: ReadonlySet<string>;
  readonly folded: ReadonlySet<string>;
  readonly productCounts: ReadonlyMap<string, number> | undefined;
  // readonly archiving: boolean;
  readonly onToggle: (categoryId: string) => void;
  // readonly onEdit: (category: Category) => void;
  // readonly onArchive: (category: Category) => void;
};

const NO_PATH = '—';

const pathOf = (category: Category, locale: string): string => {
  const slug = (category.translations.find((entry) => entry.locale === locale) ?? category.translations[0])
    ?.slug;
  return slug === undefined ? NO_PATH : `/c/${slug}`;
};

const indentOf = (depth: number): CSSProperties => ({ '--tree-depth': String(depth) }) as CSSProperties;

export const useCategoryColumns = (input: CategoryColumnsInput): readonly Column<CategoryNode>[] => {
  const { t, locale } = usePageContext();
  const labelOf = (category: Category) => nameIn(category, locale, category.id);
  const toggleOf = (category: Category) => {
    if (!input.parents.has(category.id)) return <span className={styles['toggleSpace']} />;
    const open = !input.folded.has(category.id);
    return (
      <button
        type="button"
        className={styles['toggle']}
        aria-expanded={open}
        aria-label={t('categoryList.toggle', { name: labelOf(category) })}
        onClick={(event) => {
          event.stopPropagation();
          input.onToggle(category.id);
        }}
      >
        <ChevronRight size={16} aria-hidden="true" className={styles['chevron']} />
      </button>
    );
  };
  return [
    {
      id: 'name',
      header: t('catalog.name'),
      primary: true,
      cell: ({ category, depth }) => (
        <span className={styles['treeName']} style={indentOf(depth)} data-child={depth > 0}>
          {toggleOf(category)}
          <span className={styles['folder']} aria-hidden="true">
            <Folder size={depth > 0 ? 14 : 18} />
          </span>
          <span className={styles['nameText']}>
            <span className={styles['name']}>{labelOf(category)}</span>
            <small dir="ltr">{pathOf(category, locale)}</small>
          </span>
        </span>
      ),
    },
    {
      id: 'languages',
      header: t('categoryList.languages'),
      cell: ({ category }) => (
        <span className={styles['languages']}>
          {input.locales.map((code) => {
            const present = category.translations.some((entry) => entry.locale === code);
            return (
              <span
                key={code}
                className={styles['language']}
                data-present={present}
                title={t(present ? 'categoryList.translated' : 'categoryList.untranslated', {
                  language: code.toUpperCase(),
                })}
              >
                {code.toUpperCase()}
              </span>
            );
          })}
        </span>
      ),
    },
    {
      id: 'products',
      header: t('categoryList.products'),
      numeric: true,
      cell: ({ category }) => {
        const products = input.productCounts?.get(category.id) ?? 0;
        return (
          <span className={styles['count']}>
            <strong>{formatNumber(products, locale)}</strong>
            <small>{t('categoryList.productsUnit', { count: products })}</small>
          </span>
        );
      },
    },
    {
      id: 'status',
      status: true,
      header: t('catalog.status'),
      cell: ({ category }) => (
        <Badge tone={category.status === 'active' ? 'success' : 'neutral'}>
          {t(`categoryList.status_${category.status}`)}
        </Badge>
      ),
    },
    // {
    //   id: 'actions',
    //   header: t('catalog.actions'),
    //   action: true,
    //   cell: ({ category }) => (
    //     <span className={styles['rowActions']}>
    //       <IconButton
    //         size="sm"
    //         label={t('categoryList.edit', { name: labelOf(category) })}
    //         icon={<Pencil size={16} aria-hidden="true" />}
    //         onClick={() => {
    //           input.onEdit(category);
    //         }}
    //       />
    //       {category.status === 'active' ? (
    //         <IconButton
    //           size="sm"
    //           label={t('categoryList.archive', { name: labelOf(category) })}
    //           icon={<Archive size={16} aria-hidden="true" />}
    //           disabled={input.archiving}
    //           onClick={() => {
    //             input.onArchive(category);
    //           }}
    //         />
    //       ) : null}
    //     </span>
    //   ),
    // },
  ];
};
